import React, { useEffect, useState } from "react";
import { Modal, Table, Tag, Tooltip, Button, Popconfirm, Space, message } from "antd";
import { DownloadOutlined, UndoOutlined } from "@ant-design/icons";
import moment from "moment";
import Service from "../../../service";
import useCompanyDateFormat from "../../../hooks/useCompanyDateFormat";

/**
 * Generic "Import History" modal - same list/columns as EmployeeInout.js's
 * ImportHistoryModal (File / Status / Total / Success / Failed / Date), plus
 * an Actions column (download error report / undo) since these bulk-import
 * screens don't have EmployeeInout's separate page-level history view.
 *
 * Any module reuses this by passing its own history/downloadErrors/undo
 * endpoint base paths (see service/index.js's `<module>Import*` constants).
 */
const ImportHistoryModal = ({
  visible,
  onClose,
  title = "Import History",
  historyUrl,
  downloadErrorsUrl,
  undoUrl,
  onUndo,
}) => {
  const dateFormat = useCompanyDateFormat();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchHistory = () => {
    if (!historyUrl) return;
    setLoading(true);
    Service.makeAPICall({
      methodName: Service.getMethod,
      api_url: `${historyUrl}?limit=20`,
    })
      .then((response) => {
        if (response?.data?.status === 1) {
          setHistory(response.data.data?.imports || []);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!visible) return;
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleDownloadErrors = async (record) => {
    const sessionId = record?.sessionId || record?._id;
    if (!downloadErrorsUrl || !sessionId) return;
    try {
      const response = await Service.makeAPICall({
        methodName: Service.getMethod,
        api_url: `${downloadErrorsUrl}/${sessionId}`,
      });
      const payload = response?.data;
      if (payload?.status === 1 && payload.data?.content) {
        const downloadLink = document.createElement("a");
        downloadLink.href = `data:text/csv;base64,${payload.data.content}`;
        downloadLink.download = payload.data.fileName || `import-errors-${sessionId}.csv`;
        downloadLink.style.display = "none";
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.remove();
      } else {
        message.error(payload?.message || "No error file available");
      }
    } catch {
      message.error("Failed to download error report");
    }
  };

  const handleUndo = async (record) => {
    const sessionId = record?.sessionId || record?._id;
    if (!undoUrl || !sessionId) return;
    try {
      const response = await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: `${undoUrl}/${sessionId}`,
      });
      if (response?.data?.status === 1) {
        message.success(response.data.message || "Import undone");
        fetchHistory();
        onUndo?.();
      } else {
        message.error(response?.data?.message || "Failed to undo import");
      }
    } catch {
      message.error("Failed to undo import");
    }
  };

  const statusMeta = {
    completed: { color: "green", label: "Completed" },
    failed: { color: "red", label: "Failed" },
    processing: { color: "blue", label: "Processing" },
    pending: { color: "orange", label: "Pending" },
    cancelled: { color: "default", label: "Cancelled" },
    undo_completed: { color: "gold", label: "Undone" },
  };

  const columns = [
    { title: "File", dataIndex: "originalFileName", key: "file", ellipsis: true, width: 170 },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (s) => {
        const meta = statusMeta[s] || { color: "default", label: s || "-" };
        return (
          <Tag color={meta.color} style={{ marginRight: 0, whiteSpace: "nowrap" }}>
            {meta.label}
          </Tag>
        );
      },
    },
    { title: "Total", dataIndex: "totalRecords", key: "total", width: 70, align: "center" },
    {
      title: "Success",
      dataIndex: "successfulRecords",
      key: "success",
      width: 80,
      align: "center",
      render: (n) => <span style={{ color: n > 0 ? "#389e0d" : undefined }}>{n ?? 0}</span>,
    },
    {
      title: "Failed",
      dataIndex: "failedRecords",
      key: "failed",
      width: 70,
      align: "center",
      render: (n) => <span style={{ color: n > 0 ? "#cf1322" : undefined }}>{n ?? 0}</span>,
    },
    {
      title: "Date",
      dataIndex: "uploadedAt",
      key: "date",
      width: 150,
      render: (d) => (d ? moment(d).format(`${dateFormat}, HH:mm`) : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 90,
      align: "center",
      render: (_, record) => (
        <Space size={4}>
          {record?.errorFile?.generated && (
            <Tooltip title="Download error report">
              <Button
                type="text"
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => handleDownloadErrors(record)}
              />
            </Tooltip>
          )}
          {record?.status === "completed" && record?.undoInfo?.canUndo !== false && (
            <Popconfirm
              title="Undo this import?"
              description="This reverts every record this import created/updated."
              onConfirm={() => handleUndo(record)}
              okText="Yes, undo"
              cancelText="Cancel"
            >
              <Tooltip title="Undo import">
                <Button type="text" danger size="small" icon={<UndoOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
      width={780}
    >
      <Table
        columns={columns}
        dataSource={history}
        rowKey={(r) => r.sessionId || r._id}
        loading={loading}
        pagination={false}
        size="middle"
        tableLayout="fixed"
        locale={{ emptyText: "No imports yet" }}
        scroll={{ y: 360 }}
      />
    </Modal>
  );
};

export default ImportHistoryModal;
